import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ImagePlus, Loader2, Check, ChevronsUpDown, Heart, ExternalLink, ShieldCheck } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../components/ui/command";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { MANAGERS as MANAGER_FALLBACK } from "../lib/managers";

const MAX_DESC = 500;
const DONATE_URL = "https://app.dvforms.net/api/dv/9vr302";

export default function Submit() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "",
        department: "",
        manager: "",
        email: "",
        description: "",
    });
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [consentOpen, setConsentOpen] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);
    const [donationOpen, setDonationOpen] = useState(false);
    const [managers, setManagers] = useState(() =>
        Array.isArray(MANAGER_FALLBACK) ? MANAGER_FALLBACK : []
    );

    useEffect(() => {
        api.get("/managers")
            .then(({ data }) => {
                if (Array.isArray(data) && data.length > 0) setManagers(data);
            })
            .catch(() => {});
    }, []);

    const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    const onPhoto = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            setError("Photo must be 10MB or smaller.");
            return;
        }
        if (!file.type.startsWith("image/")) {
            setError("Please upload an image file.");
            return;
        }
        setError("");
        setPhoto(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setError("");
        if (!photo) {
            setError("Please add a photo to accompany your moment.");
            return;
        }
        if (!form.manager) {
            setError("Please select your manager.");
            return;
        }
        if (form.description.length > MAX_DESC) {
            setError(`Description must be ${MAX_DESC} characters or fewer.`);
            return;
        }
        // Open consent dialog instead of submitting immediately.
        setConsentChecked(false);
        setConsentOpen(true);
    };

    const performSubmit = async () => {
        setError("");
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("name", form.name);
            fd.append("department", form.department);
            fd.append("manager", form.manager);
            fd.append("email", form.email);
            fd.append("description", form.description);
            fd.append("photo_consent", "true");
            fd.append("photo", photo);
            await api.post("/submissions", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Your moment is shared. Thank you.");
            setConsentOpen(false);
            setDonationOpen(true);
        } catch (e) {
            const msg = formatApiError(e, "Submission failed");
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDonationClose = () => {
        setDonationOpen(false);
        navigate("/submit/success");
    };

    return (
        <div className="App min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1">
                <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="submit-section">
                    <div className="label-overline">Share a moment</div>
                    <h1 className="font-serif text-4xl sm:text-5xl text-tmc-ink mt-4 leading-tight">
                        Tell us about a moment that moved you.
                    </h1>
                    <p className="mt-5 text-tmc-inkSoft text-lg leading-relaxed">
                        Your story is reviewed with care and presented anonymously to teammates. Names are
                        kept private — only your words and image are shared with the community.
                    </p>

                    <form onSubmit={onSubmit} className="mt-12 space-y-6" data-testid="submit-form">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <Field label="Your name">
                                <input
                                    required
                                    className="tmc-input"
                                    value={form.name}
                                    onChange={update("name")}
                                    data-testid="submit-name"
                                    placeholder="Jane Doe"
                                />
                            </Field>
                            <Field label="Facility / Department">
                                <input
                                    required
                                    className="tmc-input"
                                    value={form.department}
                                    onChange={update("department")}
                                    data-testid="submit-department"
                                    placeholder="Riverside Rehab — PT"
                                />
                            </Field>
                        </div>

                        <Field label="Manager">
                            <ManagerCombobox
                                value={form.manager}
                                onChange={(v) => setForm((p) => ({ ...p, manager: v }))}
                                managers={managers}
                            />
                        </Field>

                        <Field label="Email">
                            <input
                                required
                                type="email"
                                className="tmc-input"
                                value={form.email}
                                onChange={update("email")}
                                data-testid="submit-email"
                                placeholder="you@therapymgmt.com"
                            />
                        </Field>
                        <Field
                            label={`The moment (${form.description.length}/${MAX_DESC})`}
                            hint="A short story in your own words. Specifics make it real."
                        >
                            <textarea
                                required
                                rows={6}
                                maxLength={MAX_DESC}
                                className="tmc-input resize-none"
                                value={form.description}
                                onChange={update("description")}
                                data-testid="submit-description"
                                placeholder="It was a quiet Tuesday when…"
                            />
                        </Field>
                        <Field label="Photo">
                            <label
                                htmlFor="photo-upload"
                                className="relative block border border-dashed border-tmc-line rounded-2xl p-6 cursor-pointer hover:border-tmc-terracotta/60 hover:bg-tmc-sand/40 transition-colors"
                                data-testid="photo-dropzone"
                            >
                                <input
                                    id="photo-upload"
                                    type="file"
                                    accept="image/*"
                                    className="sr-only"
                                    onChange={onPhoto}
                                    data-testid="submit-photo-input"
                                />
                                {photoPreview ? (
                                    <div className="flex items-center gap-4">
                                        <img
                                            src={photoPreview}
                                            alt="preview"
                                            className="w-24 h-24 object-cover rounded-xl"
                                        />
                                        <div>
                                            <div className="font-medium text-tmc-ink">{photo?.name}</div>
                                            <div className="text-sm text-tmc-muted">
                                                Click to change · {(photo?.size / 1024 / 1024).toFixed(1)} MB
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-4 text-tmc-muted">
                                        <div className="w-12 h-12 rounded-full bg-tmc-sand flex items-center justify-center">
                                            <ImagePlus className="w-5 h-5 text-tmc-terracotta" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-tmc-ink">Add a photo</div>
                                            <div className="text-sm">JPEG, PNG, WEBP, GIF · up to 10 MB</div>
                                        </div>
                                    </div>
                                )}
                            </label>
                        </Field>

                        {error && (
                            <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm" data-testid="submit-error">
                                {error}
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-4 items-center pt-2">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="tmc-btn-primary w-full sm:w-auto"
                                data-testid="submit-form-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                                    </>
                                ) : (
                                    "Share my moment"
                                )}
                            </button>
                            <p className="text-xs text-tmc-muted">
                                By submitting, you confirm the story is yours to share and that anyone in it has consented to its telling.
                            </p>
                        </div>
                    </form>
                </section>
            </main>
            <Footer />

            {/* Photo Consent Dialog — must be acknowledged before submission completes */}
            <Dialog open={consentOpen} onOpenChange={(open) => { if (!open && !submitting) { setConsentOpen(false); setConsentChecked(false); } }}>
                <DialogContent className="max-w-lg" data-testid="consent-modal">
                    <DialogHeader>
                        <div className="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-tmc-water/15 mb-2">
                            <ShieldCheck className="w-5 h-5 text-tmc-waterDark" />
                        </div>
                        <DialogTitle className="text-center font-serif text-2xl text-tmc-ink">
                            Photo use consent
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Please review and acknowledge the photo use consent before submitting.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="rounded-xl border border-tmc-line bg-tmc-sand/40 p-5 text-tmc-inkSoft text-sm leading-relaxed">
                        By submitting a photo, you acknowledge and consent to TMC's use of the image for promotional,
                        marketing, and communication purposes, including but not limited to social media, digital
                        platforms, and print materials, without compensation.
                    </div>

                    <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
                        <Checkbox
                            checked={consentChecked}
                            onCheckedChange={(v) => setConsentChecked(v === true)}
                            className="mt-0.5"
                            data-testid="consent-checkbox"
                        />
                        <span className="text-sm text-tmc-ink">
                            I have read and agree to TMC's photo use terms above.
                        </span>
                    </label>

                    {error && (
                        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm" data-testid="consent-error">
                            {error}
                        </div>
                    )}

                    <DialogFooter className="sm:justify-between gap-2 mt-2">
                        <button
                            onClick={() => { if (!submitting) { setConsentOpen(false); setConsentChecked(false); } }}
                            disabled={submitting}
                            className="px-4 py-2 rounded-full text-sm font-medium text-tmc-inkSoft hover:bg-tmc-sand"
                            data-testid="consent-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={performSubmit}
                            disabled={!consentChecked || submitting}
                            className="tmc-btn-primary px-5"
                            data-testid="consent-confirm"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4" /> I agree, share my moment
                                </>
                            )}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Donation Modal */}
            <Dialog open={donationOpen} onOpenChange={(open) => { if (!open) handleDonationClose(); }}>
                <DialogContent className="max-w-md" data-testid="donation-modal">
                    <DialogHeader>
                        <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-tmc-terracotta/15 mb-3">
                            <Heart className="w-6 h-6 text-tmc-terracottaDark fill-tmc-terracotta/40" />
                        </div>
                        <DialogTitle className="text-center font-serif text-2xl text-tmc-ink">
                            Thank you for your submission
                        </DialogTitle>
                        <DialogDescription className="text-center text-tmc-inkSoft">
                            Your moment is now in the hands of our team. We read every story with attention.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2 rounded-xl border border-tmc-line bg-tmc-sand/40 p-5 text-center">
                        <div className="label-overline">Suggested donation</div>
                        <div className="font-serif text-4xl text-tmc-ink mt-1">$5</div>
                        <p className="text-sm text-tmc-muted mt-1">per submission</p>
                        <p className="mt-3 text-tmc-inkSoft text-sm italic">
                            Give more if you feel inspired — every dollar makes a difference 💙
                        </p>
                    </div>
                    <DialogFooter className="sm:flex-col sm:space-x-0 gap-2">
                        <a
                            href={DONATE_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="tmc-btn-primary w-full"
                            data-testid="donation-donorview-link"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Donate via Donorview
                        </a>
                        <button
                            onClick={handleDonationClose}
                            className="text-sm text-tmc-muted hover:text-tmc-ink py-2"
                            data-testid="donation-skip"
                        >
                            Continue without donating
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="label-overline block mb-2">{label}</label>
            {children}
            {hint && <p className="text-xs text-tmc-muted mt-2">{hint}</p>}
        </div>
    );
}

function ManagerCombobox({ value, onChange, managers }) {
    const [open, setOpen] = useState(false);
    const sorted = useMemo(() => {
        const list = Array.isArray(managers) ? managers : [];
        return [...list].sort((a, b) => a.localeCompare(b));
    }, [managers]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    className="tmc-input flex items-center justify-between text-left"
                    data-testid="manager-combobox-trigger"
                >
                    <span className={value ? "text-tmc-ink" : "text-tmc-muted"}>
                        {value || "Select your manager…"}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 text-tmc-muted shrink-0 ml-2" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                    <CommandInput placeholder="Search managers…" data-testid="manager-search-input" />
                    <CommandList>
                        <CommandEmpty>No manager found.</CommandEmpty>
                        <CommandGroup>
                            {sorted.map((m) => (
                                <CommandItem
                                    key={m}
                                    value={m}
                                    onSelect={(v) => {
                                        onChange(v === value ? "" : m);
                                        setOpen(false);
                                    }}
                                    data-testid={`manager-option-${m.replace(/\s+/g, "-")}`}
                                >
                                    <Check
                                        className={`mr-2 h-4 w-4 ${value === m ? "opacity-100 text-tmc-terracotta" : "opacity-0"}`}
                                    />
                                    {m}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
